import {
  Component,
  OnInit,
  Inject,
  PLATFORM_ID,
  OnDestroy,
  HostListener,
  AfterViewInit,
  ChangeDetectorRef,
  NgZone,
  ChangeDetectionStrategy,
} from '@angular/core';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { LoginComponent } from '../../../components/login/login.component';
import { BuyerVerificationService } from '../../services/buyer-verification.service';
import { BehaviorSubject, filter, Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ApiService } from '../../services/api.service';
import { LoaderService } from '../../services/loader.service';
import { AuthService } from '../../services/auth.service';
import { LogoutDetectionService } from '../../services/logout-detection.service';
import { NotificationService } from '../../services/notification.service';
import { FormsModule } from '@angular/forms';
import { BehaviourSubjectService } from '../../services/behaviour-subject.service';
import { Event as RouterEvent } from '@angular/router';
import { environment } from '../../../../environment/environment';
import { CartService } from '../../services/cart.service';
import { ModalService } from '../../services/modal.service';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [CommonModule, LoginComponent, FormsModule, RouterModule],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderComponent implements OnInit, OnDestroy {
  showHoverPopup: boolean = false;
  searchTerm = '';
  userData: any;
  showLoginModal = false;
  showBuyerVerificationModal = false;
  showSearch: boolean = false;
  isSearch: boolean = false;
  imageUrls: any;

  isModalOpen = false;
  selectedTab: 'usedParts' | 'scrap' | null = null;
  private modalHoverTimeout: any = null;
  isLoggedIn = false;
  userDataLogin: any;
  sub: Subscription = new Subscription();
  private destroy$ = new Subject<void>();
  user: any;
  userForm: any;

  // Location properties
  userLocation: string = 'Location'; // Default location text
  isLocationLoading: boolean = false;
  locationError: boolean = false;
  productData: any = null;
  currentAction: string = 'other';
  private lastScrollTop = 0;
  private isHome = false;
  environment1 = environment.s3BucketUrl;
  scrapList: any;
  usedList: any;
  showSettingsPanel = false;
  showMoreOptionsDropdown = false;
  cartItemCount: number = 0; // Add cart count property
  isCartCountLoading: boolean = false; // Add loading state for cart count
  private cartCountFetchInProgress: boolean = false; // Flag to prevent multiple simultaneous calls

  // Logout confirmation popup
  showLogoutPopup: boolean = false;

  // Debounce timer for search
  private searchDebounceTimer: any = null;
  private readonly SEARCH_DEBOUNCE_DELAY = 300; // 300ms delay

  // Search popup properties
  showSearchPopup: boolean = false;
  searchGuideData: any;
  advanceSearchData: any;
  selectedChip: any = null;
  isMobileSearchActive: boolean = false; // Track mobile search input state
  selectedSearchFields: any = {
    make: '',
    model: '',
    year_of_manufacture: '',
    variant: '',
  };
  defaultSearchImage = 'https://cdn.builder.io/api/v1/image/assets/TEMP/b18795cb56f55f03b9aaca888100d5b63db572dc?placeholderIfAbsent=true&apiKey=cb544d29e73245039207efcd0a1a6bc7';
  scrapDefaultSearchImage = 'https://cdn.builder.io/api/v1/image/assets/TEMP/3179c3111a162b89731a23e1cfd2428a324601d0?placeholderIfAbsent=true&apiKey=cb544d29e73245039207efcd0a1a6bc7';

  constructor(
    private router: Router,
    private apiService: ApiService,
    private loadService: LoaderService,
    private authService: AuthService,
    private logoutDetectionService: LogoutDetectionService,
    @Inject(PLATFORM_ID) private platformId: Object,
    private behaviourSubject: BehaviourSubjectService,
    private cdref: ChangeDetectorRef,
    private cartService: CartService,
    private ngZone: NgZone,
    private notificationService: NotificationService,
    private buyerVerificationService: BuyerVerificationService,
    private modalService: ModalService,
  ) {
    if (isPlatformBrowser(this.platformId)) {
      // Track route changes to toggle scroll behavior
      this.router.events
        .pipe(
          filter(
            (event: RouterEvent): event is NavigationEnd =>
              event instanceof NavigationEnd
          ),
          takeUntil(this.destroy$)
        )
        .subscribe((event: NavigationEnd) => {
          const currentUrl = event.urlAfterRedirects;

          this.isHome = currentUrl === '/' || currentUrl.startsWith('/home');

          if (this.isHome) {
            this.checkScrollForSearch(); // Enable scroll-based visibility
          } else {
            this.isSearch = true; // Always show on other pages
          }

          // Refresh cart count when navigating back to header (exclude /cart route)
          if (
            this.isLoggedIn &&
            (currentUrl === '/' || currentUrl.startsWith('/home')) &&
            !currentUrl.startsWith('/cart')
          ) {
            this.fetchCartCount();
          }
        });

      // Auth sync across tabs
      window.addEventListener('storage', (event) => {
        if (event.key === 'useractive') {
          setTimeout(() => {
            this.authService.initializeAuthState();
          }, 0);
        }
      });
    }
  }

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.loadScrapAndUsedParts();

      this.behaviourSubject.showLoginModal$
        .pipe(takeUntil(this.destroy$))
        .subscribe((visible) => {
          this.showLoginModal = visible;
          if (visible) {
            this.modalService.openModal('header-login-modal', () => this.closePopup());
          } else {
            this.modalService.closeModal('header-login-modal');
          }
          this.cdref.markForCheck();
        });

      this.behaviourSubject.loginSource$
        .pipe(takeUntil(this.destroy$))
        .subscribe((source) => {
          this.currentAction = source || 'other';
          this.cdref.markForCheck();
        });

      this.behaviourSubject.productData$
        .pipe(takeUntil(this.destroy$))
        .subscribe((data) => {
          this.productData = data;
          this.cdref.markForCheck();
        });

      this.buyerVerificationService.showModal$
        .pipe(takeUntil(this.destroy$))
        .subscribe((visible) => {
          this.showBuyerVerificationModal = visible;
          if (visible) {
            this.modalService.openModal('header-buyer-verification', () => this.closeBuyerVerificationModal());
          } else {
            this.modalService.closeModal('header-buyer-verification');
          }
          this.cdref.markForCheck();
        });

      // Auto-fetch user location for all visitors
      this.getUserLocation();

      // Subscribe to authentication state changes
      this.authService.isLoggedIn$
        .pipe(takeUntil(this.destroy$))
        .subscribe((isLoggedIn) => {
          this.isLoggedIn = isLoggedIn;
          if (isLoggedIn) {
            // this.notificationService.success('Welcome back!');
            this.getUserDetails();
            // Location already fetched on page load
          } else {
            this.userData = null;
            this.userDataLogin = null;
            this.user = null;
            // Reset cart count when user logs out
            const localItems = this.cartService?.getLocalStorageCartItems?.() || [];
            this.cartService.updateCartCount(localItems.length);
          }
          this.cdref.markForCheck();
        });

      // Subscribe to user data changes
      this.authService.userData$
        .pipe(takeUntil(this.destroy$))
        .subscribe((userData) => {
          this.userDataLogin = userData;
          this.userData = userData;
          this.user = userData;
          this.cdref.markForCheck();

          // Note: fetchCartCount is already called in getUserDetails(), 
          // so we don't need to call it here to avoid duplicate API calls
        });

      // Subscribe to cart count changes
      this.cartService.cartItemCount$
        .pipe(takeUntil(this.destroy$))
        .subscribe((count) => {
          this.ngZone.run(() => {
            this.cartItemCount = count;
            this.cdref.markForCheck();
          });
        });

      // Also listen for cart updates from other components
      this.behaviourSubject.productData$
        .pipe(takeUntil(this.destroy$))
        .subscribe((data) => {
          if (data && this.isLoggedIn) {
            // Refresh cart count when product data changes (e.g., after adding to cart)
            setTimeout(() => this.fetchCartCount(), 100);
          }
        });
    }
    //  this.updateSearchVisibility();
  }

  loadScrapAndUsedParts() {
    try {
      const payload = {}; // Provide appropriate payload as required by the API

      this.apiService.get_category(payload)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (value) => {
            if (value.success) {
              this.imageUrls = value?.data;
              this.scrapList = value.data.scrap_list;
              this.usedList = value.data.used_list;
              // this.notificationService.success('Categories loaded successfully');
            } else {
              console.warn('Category API returned success: false');
              this.scrapList = [];
              this.usedList = [];
              // this.notificationService.error('Failed to load categories');
            }
            this.cdref.markForCheck();
          },
          error: (err) => {
            console.error('Error loading category list:', err);
            this.scrapList = [];
            this.usedList = [];
            // this.notificationService.error(err?.error?.data?.msg || 'Error loading categories. Please try again.');
            this.cdref.markForCheck();
          },
        });
    } catch (error) {
      console.error('Error in category list:', error);
      this.scrapList = [];
      this.usedList = [];
      // this.notificationService.error('Error loading categories. Please try again.');
      this.cdref.markForCheck();
    }
  }

  // Get user's current location
  getUserLocation(): void {
    if (isPlatformBrowser(this.platformId) && 'geolocation' in navigator) {
      this.isLocationLoading = true;
      this.locationError = false;

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          this.getLocationDetails(latitude, longitude);
          this.cdref.markForCheck();
        },
        (error) => {
          console.error('Error getting location:', error);
          this.isLocationLoading = false;
          this.locationError = true;

          // Handle specific geolocation errors
          // switch (error.code) {
          //   case error.PERMISSION_DENIED:
          //     this.notificationService.info('Location permission denied. Using default location.');
          //     break;
          //   case error.POSITION_UNAVAILABLE:
          //     this.notificationService.error('Location unavailable. Using default location.');
          //     break;
          //   case error.TIMEOUT:
          //     this.notificationService.error('Location request timed out. Using default location.');
          //     break;
          //   default:
          //     this.notificationService.error('Error getting location. Using default location.');
          //     break;
          // }
          this.cdref.markForCheck();
          // Keep default location if geolocation fails
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000, // 5 minutes
        }
      );
    } else {
      this.locationError = true;
    }
  }

  // Get location details from coordinates
  getLocationDetails(latitude: number, longitude: number): void {
    const payload = {
      latitude: latitude,
      longitude: longitude,
    };

    this.apiService.geoLocation(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.isLocationLoading = false;
          if (res.data.data_by_pincode[0].city_name) {
            this.userLocation = res.data.data_by_pincode[0].city_name;
            // this.notificationService.success(`Location detected: ${this.userLocation}`);
          } else if (res?.data?.data_by_pincode[0]?.state_name) {
            this.userLocation = res.data.data_by_pincode[0].state_name;
            // this.notificationService.success(`Location detected: ${this.userLocation}`);
          } else if (res?.data?.data_by_pincode[0]?.pincode) {
            this.userLocation = res.data.data_by_pincode[0].pincode;
            // this.notificationService.success(`Location detected: ${this.userLocation}`);
          }

          localStorage.setItem(
            'userLocation',
            JSON.stringify(res.data.data_by_pincode[0])
          );

          this.cdref.markForCheck();
        },
        error: (err) => {
          console.error('Failed to fetch location details', err);
          this.isLocationLoading = false;
          this.locationError = true;
          // this.notificationService.error(err?.error?.data?.msg || 'Failed to fetch location details');
          this.cdref.markForCheck();
          // Keep default location if API fails
        },
      });
  }

  openPopup() {
    // For both mobile and desktop users, open login modal
    this.showHoverPopup = false;
    this.closeSettingsPanel();
    this.behaviourSubject.openLoginModal('other'); // No product data passed
  }

  closePopup() {
    this.modalService.closeModal('header-login-modal');
    this.behaviourSubject.closeLoginModal();
    // Login already published auth state. Re-read storage so a refresh
    // and this close stay on the same session.
    this.authService.refreshAuthState();
  }

  closeBuyerVerificationModal(): void {
    this.modalService.closeModal('header-buyer-verification');
    this.buyerVerificationService.cancelCheckoutVerification();
    this.cdref.markForCheck();
  }

  onLoginBackdropClick(event: MouseEvent): void {
    if (event.target === event.currentTarget) {
      this.closePopup();
    }
  }

  onBuyerVerificationBackdropClick(event: MouseEvent): void {
    if (event.target === event.currentTarget) {
      this.closeBuyerVerificationModal();
    }
  }

  onBuyerVerificationComplete(): void {
    this.buyerVerificationService.completeCheckoutVerification();
    this.cdref.markForCheck();
  }

  logout(): void {
    this.notificationService.success('Logging out...');
    this.logoutDetectionService.logoutWithRefresh();
  }

  confirmLogout(): void {
    this.notificationService.success('You have been logged out successfully');
    this.logoutDetectionService.logoutWithRefresh();
    this.showLogoutPopup = false;
  }

  cancelLogout(): void {
    this.showLogoutPopup = false;
  }

  openModal(tab: 'usedParts' | 'scrap') {

    // Clear any existing timeout
    if (this.modalHoverTimeout) {
      clearTimeout(this.modalHoverTimeout);
      this.modalHoverTimeout = null;
    }

    // If modal is already open for a different tab, switch to the new tab
    if (this.isModalOpen && this.selectedTab !== tab) {
      this.selectedTab = tab;
    } else {
      this.selectedTab = tab;
      this.isModalOpen = true;
    }
  }

  closeModal() {
    // Clear any existing timeout
    if (this.modalHoverTimeout) {
      clearTimeout(this.modalHoverTimeout);
      this.modalHoverTimeout = null;
    }

    this.isModalOpen = false;
    this.selectedTab = null;
  }

  onNavMouseLeave() {
    // Add a small delay before closing to allow moving to modal
    this.modalHoverTimeout = setTimeout(() => {
      if (this.isModalOpen) {
        this.closeModal();
      }
    }, 150);
  }

  onModalMouseEnter() {
    // Clear timeout when entering modal area
    if (this.modalHoverTimeout) {
      clearTimeout(this.modalHoverTimeout);
      this.modalHoverTimeout = null;
    }
  }

  onModalMouseLeave() {
    // Close modal when leaving modal area
    this.closeModal();
  }

  onModalContentMouseEnter() {
    // Clear timeout when entering modal content
    if (this.modalHoverTimeout) {
      clearTimeout(this.modalHoverTimeout);
      this.modalHoverTimeout = null;
    }
  }

  onModalContentMouseLeave() {
    // Add delay when leaving modal content
    this.modalHoverTimeout = setTimeout(() => {
      this.closeModal();
    }, 100);
  }

  gotoProductlist(data: any, type?: any) {
    this.closeModal();
    localStorage.removeItem('category');
    localStorage.removeItem('filterState')
    localStorage.removeItem('guideSearchData');
    localStorage.removeItem('mostSearchedModel');
    localStorage.removeItem('seemore');
    localStorage.setItem('category', JSON.stringify(data));
    localStorage.removeItem('categoryType');
    localStorage.removeItem('SpareCategory');
    localStorage.removeItem('MyGarageData');

    localStorage.setItem('categoryType', JSON.stringify(type));

    // Clear search text when switching categories/part types
    this.behaviourSubject.setSearchTerm('');
    this.behaviourSubject.clearSelectedFilters();

    // this.notificationService.info(`Browsing ${data?.name || type || 'products'}`);

    // Check if we're already on the product-list page
    if (this.router.url === '/product-list') {
      // Use behavior subject to notify product-list component
      this.behaviourSubject.setCategoryChanged({ data, type });
    } else {
      this.router.navigate(['/product-list']);
    }

    this.closeSettingsPanel()
  }

  gotoHome() {
    this.router.navigate(['/home']);
  }

  navigateToTataMotors() {
    window.open('https://www.tatamotors.com/', '_blank');
  }

  navigateToTata() {
    window.open('https://www.tata.com/', '_blank');
  }

  capitalizeFirstLetter(text: string | undefined): string {
    if (!text) return '';
    return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
  }

  gotoNotification(event?: Event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.closeMobileSearch();
    this.closeSettingsPanel();
    this.ngZone.run(() => {
      this.router.navigate(['/cart']);
    });
  }

  gotoUserProfile() {
    this.router.navigate(['/user-profile']);
  }

  getUserDetails(): void {
    const payload = {};
    this.loadService.show();
    this.apiService.getUserDetails(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.userDataLogin = res?.data;
          this.userData = res?.data;
          this.user = res?.data;
          this.loadService.hide();
          this.cdref.markForCheck();

          // After getting user details, fetch cart count
          this.fetchCartCount();
        },
        error: (err) => {
          console.error('Failed to fetch user details', err);
          // this.notificationService.error(err?.error?.data?.msg || 'Failed to fetch user details');
          this.loadService.hide();
          this.cdref.markForCheck();
        },
      });
  }

  // Method to fetch cart count from API
  fetchCartCount(): void {
    // Don't fetch cart count if on /cart route
    const currentRoute = this.router.url;
    if (currentRoute.startsWith('/cart')) {
      return;
    }

    // Prevent multiple simultaneous calls
    if (this.cartCountFetchInProgress || !this.isLoggedIn) {
      return;
    }

    this.cartCountFetchInProgress = true;
    this.isCartCountLoading = true;
    const payload = {
      "offset": 0, "limit": 100, "action_type": "GET_LIST", "get_selected_parts": ""
    };


    this.apiService.getCartData(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.isCartCountLoading = false;
          this.cartCountFetchInProgress = false;
          const rawItems = Array.isArray(res?.data)
            ? res.data
            : (res?.data?.cart_items || res?.data?.cart_item_list || res?.data?.items || res?.data?.cart || []);
            const totalResult = Number(
              res?.data?.total_result ??
              res?.data?.total_results ??
              res?.data?.total_count ??
              res?.data?.total_records ??
              -1,
            );
            if (res?.success && rawItems && rawItems.length > 0 && totalResult !== 0) {
              const count = rawItems.length;
              this.cartService.updateCartCount(count);
            } else {
              if (this.isLoggedIn) {
                this.cartService.updateCartCount(0);
              } else {
                const localItems = this.cartService?.getLocalStorageCartItems?.() || [];
                this.cartService.updateCartCount(localItems.length);
              }
            }
            this.cdref.markForCheck();
          },
          error: (err: any) => {
            console.error('Failed to fetch cart count:', err);
            // this.notificationService.error(err?.error?.data?.msg || 'Failed to fetch cart count');
            this.isCartCountLoading = false;
            this.cartCountFetchInProgress = false;
            this.cdref.markForCheck();
          }
        });
  }

  onSearchInput(event: any) {
    // Clear previous search fields when new search input starts
    this.selectedSearchFields = {
      make: '',
      model: '',
      year_of_manufacture: '',
      variant: '',
    };
    this.selectedChip = null;
    // Clear previous advance search data
    this.advanceSearchData = null;

    // Clear previous debounce timer
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }

    // Only call API if search term is not empty
    if (!this.searchTerm.trim()) {
      this.advanceSearchData = null;
      this.showSearchPopup = false;
      return;
    }

    // Set new debounce timer
    this.searchDebounceTimer = setTimeout(() => {
      // Call advance search with empty fields for fresh search
      this.advanceSearch({
        make: '',
        model: '',
        year_of_manufacture: '',
        variant: '',
      });
    }, this.SEARCH_DEBOUNCE_DELAY);
  }

  onEnterKey(event: any) {
    event.preventDefault();
    this.onSearchTrigger();
  }

  filterProducts(term: string) {
    const trimmedTerm = term.trim();
    if (!trimmedTerm) {
      this.notificationService.info('Please enter a search term');
      return;
    }

    // Follow the same pattern as home banner navigation
    localStorage.removeItem('filterState');
    localStorage.removeItem('category');
    localStorage.removeItem('guideSearchData');
    localStorage.removeItem('categoryType');
    localStorage.removeItem('seemore');
    localStorage.removeItem('MyGarageData');
    localStorage.removeItem('SpareCategory');
    localStorage.removeItem('mostSearchedModel');


    // ...existing code...

    // this.notificationService.success(`Searching for "${trimmedTerm}"`);

    // Clear all selected filters when searching from header
    this.behaviourSubject.clearSelectedFilters();
    this.behaviourSubject.setSearchTerm(trimmedTerm);

    // Check if we're already on the product-list page
    if (this.router.url === '/product-list' || this.router.url.startsWith('/product-list')) {
      // Only set the search term; product-list will react to searchTerm$
      // No need to call setCategoryChanged for search
    } else {
      this.router.navigate(['/product-list']);
    }

    this.searchTerm = '';
    this.showSearchPopup = false;
    this.advanceSearchData = null;
    this.selectedChip = null;
    this.selectedSearchFields = {
      make: '',
      model: '',
      year_of_manufacture: '',
      variant: '',
    };
    this.cdref.markForCheck();
  }

  // Search popup methods
  onFocus() {
    this.showSearchPopup = true;
    this.cdref.markForCheck();
    if (!this.searchGuideData) {
      this.getGuideSearchData();
    }
  }

  onBlur() {
    // Optional: Delay to allow clicks inside popup
    setTimeout(() => {
      this.showSearchPopup = false;
      this.cdref.markForCheck();
    }, 200);
  }

  onSearchTrigger() {
    if (this.searchTerm.trim()) {
      this.filterProducts(this.searchTerm);
      // Close mobile search after triggering search
      if (this.isMobileSearchActive) {
        this.closeMobileSearch();
      }
    }
  }

  advanceSearch(fields = this.selectedSearchFields) {
    if (!this.searchTerm.trim()) {
      return;
    }

    const data = {
      make: fields.make || '',
      model: fields.model || '',
      year_of_manufacture: fields.year_of_manufacture || '',
      variant: fields.variant || '',
      is_seller: true,
      part_type: 'Scrap Part',//fields.make ? (this.advanceSearchData?.suggestions?.[0]?.part_type || '') : '',
      search_text: this.searchTerm.trim(),
    };

    this.apiService.getadvancePartSearch(data)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (value) => {
          if (value.success) {
            this.advanceSearchData = value.data;
            this.showSearchPopup = true;
          } else {
            this.advanceSearchData = { smart_fill: [], suggestions: [] };
          }
          this.cdref.markForCheck();
        },
        error: (err) => {
          console.error('Error in advance search:', err);
          this.advanceSearchData = { smart_fill: [], suggestions: [] };
          this.cdref.markForCheck();
        }
      });
  }

  getGuideSearchData() {
    const data = {};
    this.apiService.guideSearch(data)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (value) => {
          if (value.success) {
            this.searchGuideData = value.data;
          } else {
            this.searchGuideData = null;
          }
          this.cdref.markForCheck();
        },
        error: (err) => {
          console.error('Error in guide search:', err);
          this.searchGuideData = null;
          this.cdref.markForCheck();
        }
      });
  }

  onChipClick(chip: any) {
    this.selectedChip = chip === this.selectedChip ? null : chip;
    this.cdref.markForCheck();

    if (chip) {
      // Structure the data from smart_fill chip
      const chipData = {
        make: chip.value || '',
        model: '',
        year_of_manufacture: '',
        variant: '',
        part_type: chip.part_type || ''
      };

      // this.notificationService.info(`Searching for ${chip.value}`);
      // Navigate with chip data
      this.gotoProductList(chipData, chip.part_type);
    }
  }

  onSuggestionClick(suggestion: any) {
    this.cdref.markForCheck();
    const searchInfo = [suggestion.make, suggestion.model, suggestion.variant].filter(Boolean).join(' ');
    // this.notificationService.info(`Searching for ${searchInfo}`);

    // Structure the data from suggestion
    const suggestionData = {
      make: suggestion.make || '',
      model: suggestion.model || '',
      year_of_manufacture: suggestion.year || '',
      variant: suggestion.variant || '',
      part_type: suggestion.part_type || ''
    };

    // Navigate with suggestion data
    this.gotoProductList(suggestionData, suggestion.part_type);
  }

  gotoProductList(item: any, type: string) {
    // Follow the same pattern as home banner
    localStorage.removeItem('filterState');
    localStorage.removeItem('category');
    localStorage.removeItem('guideSearchData');
    localStorage.removeItem('categoryType');
    localStorage.removeItem('seemore');
    localStorage.removeItem('MyGarageData');
    localStorage.removeItem('SpareCategory');
    localStorage.removeItem('mostSearchedModel');
    const searchData = {
      make: item?.make || '',
      model: item?.model || '',
      year_of_manufacture: item?.year_of_manufacture || '',
      variant: item?.variant || '',
      part_type: item?.make ? item?.part_type : ''
    };
    localStorage.setItem('guideSearchData', JSON.stringify(searchData));

    // Check if we're already on the product-list page
    if (this.router.url === '/product-list' || this.router.url.startsWith('/product-list')) {
      // Use behavior subject to notify product-list component
      this.behaviourSubject.setCategoryChanged({ ...item, type: type, isItemClick: true });
    } else {
      this.router.navigate(['/product-list']);
    }
    this.searchTerm = '';
    this.showSearchPopup = false;
    this.advanceSearchData = null;
    this.selectedChip = null;
    this.selectedSearchFields = {
      make: '',
      model: '',
      year_of_manufacture: '',
      variant: '',
    };
    // Close mobile search when navigating
    if (this.isMobileSearchActive) {
      this.closeMobileSearch();
    }
  }

  seeMoregotoProductList(section: string) {
    // this.notificationService.info(`Viewing ${section} products`);
    // Handle see more navigation
    localStorage.removeItem('filterState');
    localStorage.removeItem('category');
    localStorage.removeItem('guideSearchData');
    localStorage.removeItem('categoryType');
    localStorage.removeItem('seemore');
    localStorage.removeItem('MyGarageData');
    localStorage.removeItem('SpareCategory');
    localStorage.removeItem('mostSearchedModel');

    const searchData = section
    localStorage.setItem('seemore', searchData);

    // Check if we're already on the product-list page
    if (this.router.url === '/product-list' || this.router.url.startsWith('/product-list')) {
      // Use behavior subject to notify product-list component
      this.behaviourSubject.setCategoryChanged({ section: section, isSeeMore: true });
    } else {
      this.router.navigate(['/product-list']);
    }
    this.showSearchPopup = false;
    this.advanceSearchData = null;
    this.selectedChip = null;
    this.selectedSearchFields = {
      make: '',
      model: '',
      year_of_manufacture: '',
      variant: '',
    };
    // Close mobile search when navigating
    if (this.isMobileSearchActive) {
      this.closeMobileSearch();
    }
    this.cdref.markForCheck();
  }

  ngOnDestroy() {
    // Clean up all subscriptions
    this.destroy$.next();
    this.destroy$.complete();

    if (this.sub) {
      this.sub.unsubscribe();
    }

    // Clear any pending timeouts
    if (this.modalHoverTimeout) {
      clearTimeout(this.modalHoverTimeout);
      this.modalHoverTimeout = null;
    }

    // Clear search debounce timer
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = null;
    }
  }

  @HostListener('window:scroll', [])
  onWindowScroll(): void {
    if (!this.isHome) return; // ✅ Disable scroll logic outside /home

    const scrollTop = window.scrollY || document.documentElement.scrollTop;

    // ✅ Show on scroll down
    if (scrollTop > 100) {
      this.isSearch = true;
    }
    // ✅ Hide on scroll near top
    else if (scrollTop < 50) {
      this.isSearch = false;
    }

    this.lastScrollTop = scrollTop;
  }

  private checkScrollForSearch(): void {
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    this.isSearch = scrollTop > 100 ? true : false;
  }

  toggleSettingsPanel() {
    this.showSettingsPanel = !this.showSettingsPanel;
  }

  closeSettingsPanel() {
    this.showSettingsPanel = false;
  }

  openSection: string | null = null;

  sections = [
    // { key: 'used', label: 'Used Parts' },
    { key: 'scrap', label: 'Scrap' },
    { key: 'more', label: 'More Options' },
  ];

  toggleSection(key: string) {
    this.openSection = this.openSection === key ? null : key;
  }

  toggleMoreOptionsDropdown() {
    this.showMoreOptionsDropdown = !this.showMoreOptionsDropdown;
  }

  closeMoreOptionsDropdown() {
    this.showMoreOptionsDropdown = false;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    // Close dropdown when clicking outside
    const target = event.target as HTMLElement;
    if (!target.closest('.more-options-container')) {
      this.showMoreOptionsDropdown = false;
    }
  }

  // Navigation methods for dropdown items
  navigateToProfile() {
    this.showMoreOptionsDropdown = false;
    this.closeSettingsPanel();
    this.router.navigate(['/user-profile']);
  }

  navigateToOrders() {
    this.showMoreOptionsDropdown = false;
    this.closeSettingsPanel();
    this.router.navigate(['/user-profile'], { queryParams: { section: 'orders' } });
  }

  navigateToGarage() {
    this.showMoreOptionsDropdown = false;
    this.closeSettingsPanel();
    this.router.navigate(['/user-profile'], {
      queryParams: { section: 'my-garage' },
    });
  }

  navigateToAddresses() {
    this.showMoreOptionsDropdown = false;
    this.closeSettingsPanel();
    this.router.navigate(['/user-profile'], { queryParams: { section: 'address' } });
  }

  navigateToWishlist() {
    this.showMoreOptionsDropdown = false;
    this.closeSettingsPanel();
    this.router.navigate(['/user-profile'], {
      queryParams: { section: 'wishlist' },
    });
  }

  navigateToCoupons() {
    this.showMoreOptionsDropdown = false;
    this.closeSettingsPanel();
    this.router.navigate(['/user-profile'], { queryParams: { section: 'coupons' } });
  }

  navigateToNotifications() {
    this.showMoreOptionsDropdown = false;
    this.closeSettingsPanel();
    this.router.navigate(['/user-profile'], {
      queryParams: { section: 'notifications' },
    });
  }

  navigateToScrapCertificate() {
    this.showMoreOptionsDropdown = false;
    this.closeSettingsPanel();
    this.router.navigate(['/user-profile'], {
      queryParams: { section: 'scrap-certificate' },
    });
  }

  navigateToFAQ() {
    this.showMoreOptionsDropdown = false;
    this.closeSettingsPanel();
    this.router.navigate(['/user-profile'], {
      queryParams: { section: 'profile-faq' },
    });
  }

  navigateToCustomerSupport() {
    this.showMoreOptionsDropdown = false;
    this.closeSettingsPanel();
    this.router.navigate(['/user-profile'], {
      queryParams: { section: 'customer-support' },
    });
  }

  handleLogout() {
    this.showMoreOptionsDropdown = false;
    this.closeSettingsPanel();
    this.showLogoutPopup = true;
  }

  // Method to manually refresh cart count (can be called from template or other places)
  refreshCartCount(): void {
    if (this.isLoggedIn) {
      this.fetchCartCount();
    } else {
    }
  }

  // Toggle mobile search input
  toggleMobileSearch() {
    this.isMobileSearchActive = !this.isMobileSearchActive;
    if (this.isMobileSearchActive) {
      // Focus the input after it's rendered
      setTimeout(() => {
        const mobileSearchInput = document.querySelector('#mobile-search-input') as HTMLInputElement;
        if (mobileSearchInput) {
          mobileSearchInput.focus();
        }
      }, 100);
    } else {
      // Close search popup when closing mobile search
      this.showSearchPopup = false;
      this.searchTerm = '';
    }
  }

  // Close mobile search
  closeMobileSearch() {
    this.isMobileSearchActive = false;
    this.showSearchPopup = false;
  }
}
